import { inject, Injectable } from '@angular/core';
import { StorageService } from 'app/core/services/storage/storage.service';
import { NATIONAL_GREETINGS, NationalDate } from './national-greetings';

/** Time-of-day buckets used to pick the normal greeting. */
export type TimeRange = 'morning' | 'afternoon' | 'night';

/** The greeting to render: already-resolved text plus its emoji. */
export interface ResolvedGreeting {
  /**
   * For national dates, the literal in-language message. Otherwise, an i18n
   * key that the caller must translate (e.g. via the `translate` pipe).
   */
  text: string;
  /** Emoji to render next to the text. Empty on national dates, which use the
   *  flag instead. */
  emoji: string;
  /**
   * Country whose flag belongs next to the text, or null.
   *
   * A country id rather than a flag emoji: Windows ships no glyphs for
   * regional-indicator pairs, so 🇪🇨 renders as the letters "EC" there — which
   * is what most of this app's users saw. The caller draws it instead.
   */
  flag: string | null;
  /** True when the text is a national-date message. */
  isNationalDate: boolean;
}

/** Options for {@link GreetingService.resolveGreeting}. */
export interface ResolveGreetingOptions {
  /** Enable the Home-only "coming back the same day" greeting. */
  trackReturnVisit?: boolean;
  /** Override "now" (tests). Defaults to the current date/time. */
  now?: Date;
  /** Override the timezone (tests). Defaults to the browser timezone. */
  timeZone?: string;
}

/**
 * Resolves the welcome greeting shared by the Home and Instance-selector
 * heroes. Resolution order: national date, then (optionally) a return-visit
 * greeting, then a time-of-day greeting.
 */
@Injectable({ providedIn: 'root' })
export class GreetingService {
  private _storage = inject(StorageService);

  /** A same-day gap at or above this reads as "coming back", not a reload. */
  private static readonly RETURN_VISIT_GAP_MS = 30 * 60 * 1000;
  private static readonly LAST_VISIT_KEY = 'primecontrol_homeLastVisit';

  private static readonly RETURN_GREETINGS = [
    'GREETING_RETURN_1',
    'GREETING_RETURN_2',
    'GREETING_RETURN_3',
  ];

  private static readonly GREETINGS_BY_RANGE: Record<TimeRange, string[]> = {
    morning: [
      'GREETING_MORNING_1',
      'GREETING_MORNING_2',
      'GREETING_MORNING_3',
      'GREETING_MORNING_4',
      'GREETING_MORNING_5',
    ],
    afternoon: [
      'GREETING_AFTERNOON_1',
      'GREETING_AFTERNOON_2',
      'GREETING_AFTERNOON_3',
      'GREETING_AFTERNOON_4',
      'GREETING_AFTERNOON_5',
    ],
    night: [
      'GREETING_NIGHT_1',
      'GREETING_NIGHT_2',
      'GREETING_NIGHT_3',
      'GREETING_NIGHT_4',
      'GREETING_NIGHT_5',
    ],
  };

  private static readonly EMOJIS_BY_RANGE: Record<TimeRange, string[]> = {
    morning: ['👋', '☀️', '🌅', '☕', '✨'],
    afternoon: ['👋', '🙌', '😊', '✨', '🌤️'],
    night: ['👋', '🌙', '🌟', '✨', '😊'],
  };

  /** Resolve the greeting to render for this screen visit. */
  resolveGreeting(options: ResolveGreetingOptions = {}): ResolvedGreeting {
    const now = options.now ?? new Date();
    const timeZone = options.timeZone ?? this._detectTimeZone();

    const national = this._resolveNationalDate(now, timeZone);
    if (national) {
      // No emoji on a national date: the flag goes in as a drawn chip, because
      // the flag EMOJI renders as its two letters ("EC") on Windows.
      return {
        text: national.date.message,
        emoji: '',
        flag: national.country,
        isNationalDate: true,
      };
    }

    const range = this._timeRange(now);
    const emoji = this._pick(GreetingService.EMOJIS_BY_RANGE[range]);

    if (options.trackReturnVisit && this._isReturningVisit(now)) {
      return {
        text: this._pick(GreetingService.RETURN_GREETINGS),
        emoji,
        flag: null,
        isNationalDate: false,
      };
    }

    return {
      text: this._pick(GreetingService.GREETINGS_BY_RANGE[range]),
      emoji,
      flag: null,
      isNationalDate: false,
    };
  }

  /** The national date matching today for the timezone's country, if any. */
  private _resolveNationalDate(
    now: Date,
    timeZone: string
  ): { date: NationalDate; country: string } | null {
    const country = NATIONAL_GREETINGS.find((c) => c.timezones.includes(timeZone));
    if (!country) {
      return null;
    }
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const date = country.dates.find((d) => d.month === month && d.day === day);
    return date ? { date, country: country.country } : null;
  }

  /**
   * Whether this is a same-day return visit (≥30 min since the last one).
   * Always records "now" as the latest visit.
   */
  private _isReturningVisit(now: Date): boolean {
    const nowMs = now.getTime();
    const last = Number(this._storage.getItem(GreetingService.LAST_VISIT_KEY)) || 0;
    const sameDay = new Date(last).toDateString() === new Date(nowMs).toDateString();
    const returning = sameDay && nowMs - last >= GreetingService.RETURN_VISIT_GAP_MS;
    this._storage.setItems(GreetingService.LAST_VISIT_KEY, String(nowMs));
    return returning;
  }

  /** morning (5:00-11:59), afternoon (12:00-18:59) or night (19:00-4:59). */
  private _timeRange(now: Date): TimeRange {
    const hour = now.getHours();
    if (hour >= 5 && hour < 12) {
      return 'morning';
    }
    if (hour >= 12 && hour < 19) {
      return 'afternoon';
    }
    return 'night';
  }

  /** Browser timezone, or '' when unavailable. */
  private _detectTimeZone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
      return '';
    }
  }

  private _pick<T>(pool: T[]): T {
    return pool[Math.floor(Math.random() * pool.length)];
  }
}
